/**
 * Shared country-aware phone validation for auth flows (signup + phone/OTP
 * login). Both paths now run through the single PhoneAuthFlow entry point, which
 * is the only consumer — kept as its own module so the rules stay unit-testable
 * apart from the component.
 */

// Longest national-number length we accept (used to cap the phone input).
export const MAX_PHONE_DIGITS = 15;

// Per-country exact/allowed digit counts. Returns a human-readable error
// (empty string when valid).
export const validatePhoneForCountry = (countryCode: string, phoneValue: string): string => {
  const digits = (phoneValue || '').replace(/\D/g, '');
  if (digits.length === 0) return 'Please enter your phone number';

  const rules: Record<string, { min: number; max: number; message: string }> = {
    '+91': { min: 10, max: 10, message: 'Indian phone numbers must be exactly 10 digits' },
    '+1': { min: 10, max: 10, message: 'US/Canada phone numbers must be exactly 10 digits' },
    '+44': { min: 10, max: 10, message: 'UK phone numbers must be exactly 10 digits' },
    '+86': { min: 11, max: 11, message: 'Chinese phone numbers must be exactly 11 digits' },
    '+81': { min: 10, max: 10, message: 'Japanese phone numbers must be exactly 10 digits' },
    '+49': { min: 10, max: 11, message: 'German phone numbers must be 10-11 digits' },
    '+33': { min: 9, max: 9, message: 'French phone numbers must be exactly 9 digits' },
    '+39': { min: 9, max: 10, message: 'Italian phone numbers must be 9-10 digits' },
    '+34': { min: 9, max: 9, message: 'Spanish phone numbers must be exactly 9 digits' },
    '+55': { min: 10, max: 11, message: 'Brazilian phone numbers must be 10-11 digits' },
    '+61': { min: 9, max: 9, message: 'Australian phone numbers must be exactly 9 digits' },
    '+7': { min: 10, max: 10, message: 'Russian/Kazakh phone numbers must be exactly 10 digits' },
    '+82': { min: 9, max: 10, message: 'South Korean phone numbers must be 9-10 digits' },
    '+65': { min: 8, max: 8, message: 'Singapore phone numbers must be exactly 8 digits' },
    '+971': { min: 9, max: 9, message: 'UAE phone numbers must be exactly 9 digits' },
    '+966': { min: 9, max: 9, message: 'Saudi phone numbers must be exactly 9 digits' },
    '+27': { min: 9, max: 9, message: 'South African phone numbers must be exactly 9 digits' },
    '+52': { min: 10, max: 10, message: 'Mexican phone numbers must be exactly 10 digits' },
    '+54': { min: 10, max: 10, message: 'Argentine phone numbers must be exactly 10 digits' },
    '+56': { min: 9, max: 9, message: 'Chilean phone numbers must be exactly 9 digits' },
  };

  const rule = rules[countryCode];
  if (rule) {
    if (digits.length < rule.min || digits.length > rule.max) return rule.message;
  } else if (digits.length < 7 || digits.length > 15) {
    return 'Phone number must be between 7-15 digits';
  }
  return '';
};

// Per-country max digit count (used for the LIVE inline error while typing —
// we only nag when the user EXCEEDS the max, never for "still too short").
const PHONE_MAX_DIGITS_BY_COUNTRY: Record<string, number> = {
  '+91': 10, '+1': 10, '+44': 10, '+86': 11, '+81': 10, '+49': 11, '+33': 9,
  '+39': 10, '+34': 9, '+55': 11, '+61': 9, '+7': 10, '+82': 10, '+65': 8,
  '+971': 9, '+966': 9, '+27': 9, '+52': 10, '+54': 10, '+56': 9,
};

// Returns the country-specific error ONLY when the entered number is longer
// than that country allows; empty string otherwise (including too-short).
export const phoneOverLengthError = (countryCode: string, phoneValue: string): string => {
  const digits = (phoneValue || '').replace(/\D/g, '');
  const max = PHONE_MAX_DIGITS_BY_COUNTRY[countryCode] ?? 15;
  if (digits.length > max) {
    return validatePhoneForCountry(countryCode, phoneValue);
  }
  return '';
};
